import { MprcRequestErrorDialog, MprcSpinnerIndicator } from "components";
import { ChannelPanelDto, TopicPanelDto, UserPanelSpecialistInterestDtoPaginatedList } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FeedBoxPanels, FeedDrawerPanels } from "views/knowledge/feed/components/FeedBoxPanels";
import { PanelChannels } from "views/knowledge/feed/components/Panel/PanelChannels";
import { PanelTopics } from "views/knowledge/feed/components/Panel/PanelTopics";
import { PanelTopicSpecialistInterest } from "views/knowledge/feed/components/Panel/PanelTopicSpecialistInterest";

interface IProps {
    topicId: string;
    renderMode: "default" | "drawer";
    onEmptyPanel: () => void;
}

/**
 * Painel lateral para o feed de Tópico.
 * @param props
 */
export const FeedTopicRightPanel: React.FC<IProps> = (props) => {

    const { topicId, renderMode, onEmptyPanel } = props;

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");

    const [viewError, setViewError] = useState<any>();
    const [loading, setLoading] = useState<boolean>(true);
    const [channelMostTalked, setChannelMostTalked] = useState<ChannelPanelDto[]>([]);
    const [topicsRelated, setTopicsRelated] = useState<TopicPanelDto[]>([]);
    const [specialistInterest, setSpecialistInterest] = useState<UserPanelSpecialistInterestDtoPaginatedList>();

    ////////////////////////////////////////////////////////////////////////////
    /// Effects
    ////////////////////////////////////////////////////////////////////////////

    // Ao carregar
    useEffect(() => {
        if (loading) {
            Promise.all([
                api.v1.knowledgeFeed.getFeedTopicPanelChannelMostMention({ topicId }),
                api.v1.knowledgeFeed.getFeedTopicPanelRelation({ topicId }),
                api.v1.knowledgeFeed.getFeedTopicPanelSpecialistInterest({ topicId, searchText: null, pageNumber: 1, pageSize: 10 })
            ])
                .then((results) => {
                    setChannelMostTalked(results[0].data);
                    setTopicsRelated(results[1].data);
                    setSpecialistInterest(results[2].data);

                    if (results[0].data.length == 0
                        && results[1].data.length == 0
                        && results[2].data.items.length == 0) {
                        onEmptyPanel();
                    }
                })
                .catch((error: any) => {
                    setViewError(error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [loading]);

    const panels = useMemo(() => {
        const data = [];

        if (specialistInterest && specialistInterest.totalPages > 0) {
            data.push(
                <PanelTopicSpecialistInterest
                    title={t("Feed.Panels.TopicSpecialistInterest.Title")}
                    topicId={topicId}
                    viewData={specialistInterest!}
                    onChange={(item, index) => {
                        let newItems = [...specialistInterest.items];
                        newItems[index] = item;
                        setSpecialistInterest({ ...specialistInterest, items: newItems });
                    }}
                />
            );
        }

        if (channelMostTalked.length > 0) {
            data.push(
                <PanelChannels
                    title={t("Feed.Panels.ChannelMostMention.Title")}
                    viewData={channelMostTalked}
                />
            );
        }

        if (topicsRelated.length > 0) {
            data.push(<PanelTopics
                title={t("Feed.Panels.TopicsRelation.Title")}
                viewData={topicsRelated}
            />);
        }

        return data;
    }, [specialistInterest, channelMostTalked, topicsRelated]);

    ////////////////////////////////////////////////////////////////////////////
    /// Render
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcRequestErrorDialog
                error={viewError}
                onClose={() => setViewError(undefined)}
            />

            {
                loading
                    ? <MprcSpinnerIndicator />
                    : renderMode == "default"
                        ? <FeedBoxPanels>{panels}</FeedBoxPanels>
                        : <FeedDrawerPanels>{panels}</FeedDrawerPanels>
            }
        </>
    );
};
